/**
 * TerrainForge — 하이트맵 지형.
 *
 * 하이트맵(회색 이미지, 밝을수록 높음) 한 장이 야외 필드가 된다. 이미지가 없으면 fBm 노이즈로
 * 섬 하나를 만든다. 텍스처는 스플랫 — 높이와 경사로 풀·흙·바위·눈 네 층을 섞는데,
 * 셰이더에서 섞지 않고 **알베도 한 장으로 굽는다**. 그래야 뷰어와 glb 가 같은 그림이고,
 * 엔진에 넣었을 때 커스텀 셰이더가 필요 없다.
 *
 * 순수 함수(Field/캔버스)만 쓴다 — 테스트가 헤드리스로 돈다.
 */

import * as THREE from 'three';
import type { Field, Raster } from '../types.ts';
import { gaussianBlur, makeField, resampleField, sampleField } from '../imaging/raster.ts';

export interface TerrainParams {
  /** 한 변 길이(월드 단위) */
  size: number;
  /** 격자 한 변 정점 수 */
  resolution: number;
  heightScale: number;
  /** 하이트맵 스무딩 시그마(픽셀). 생성 이미지의 JPEG 얼룩을 지운다 */
  smooth: number;
  /** 수면 높이 (0..1, 정규화 높이). 0 이면 없음 */
  waterLevel: number;
  /** 스플랫 문턱 (정규화 높이 / 경사) */
  sandHeight: number;
  rockSlope: number;
  snowHeight: number;
  /** 스플랫 텍스처 반복 수 */
  tiling: number;
  /** 가장자리를 바다로 떨어뜨린다 (섬) */
  island: number;
}

export const DEFAULT_TERRAIN: TerrainParams = {
  size: 64, resolution: 129, heightScale: 9, smooth: 1.2, waterLevel: 0.22,
  sandHeight: 0.28, rockSlope: 0.55, snowHeight: 0.8, tiling: 12, island: 0.6,
};

/** 회색 이미지 → 정규화 높이장(0..1) res×res */
export function heightFieldFromRaster(src: Raster, res: number, smooth = 0, island = 0): Field {
  const { width: w, height: h, data } = src;
  const f = makeField(w, h);
  for (let i = 0; i < w * h; i++) f.data[i] = (0.2126 * data[i * 4]! + 0.7152 * data[i * 4 + 1]! + 0.0722 * data[i * 4 + 2]!) / 255;
  return finishHeight(f, res, smooth, island);
}

/** fBm 노이즈 섬 — 이미지 없이도 지형이 선다. 결정론(seed). */
export function fbmHeightField(res: number, seed = 7, island = 0.6): Field {
  const f = makeField(res, res);
  const rnd = mulberry(seed);
  const perm = new Uint8Array(512);
  const p = Array.from({ length: 256 }, (_, i) => i);
  for (let i = 255; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [p[i], p[j]] = [p[j]!, p[i]!]; }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]!;
  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const grad = (hash: number, x: number, y: number) => { const h = hash & 3; return (h & 1 ? -x : x) + (h & 2 ? -y : y); };
  const noise = (x: number, y: number) => {
    const X = Math.floor(x) & 255, Y = Math.floor(y) & 255; x -= Math.floor(x); y -= Math.floor(y);
    const u = fade(x), v = fade(y);
    const a = perm[X]! + Y, b = perm[X + 1]! + Y;
    const l = (t: number, p0: number, p1: number) => p0 + t * (p1 - p0);
    return l(v, l(u, grad(perm[a]!, x, y), grad(perm[b]!, x - 1, y)), l(u, grad(perm[a + 1]!, x, y - 1), grad(perm[b + 1]!, x - 1, y - 1)));
  };
  let lo = Infinity, hi = -Infinity;
  for (let y = 0; y < res; y++) for (let x = 0; x < res; x++) {
    let v = 0, amp = 1, fr = 3 / res;
    for (let o = 0; o < 6; o++) { v += amp * noise(x * fr + 13.7, y * fr + 5.1); amp *= 0.5; fr *= 2; }
    f.data[y * res + x] = v; lo = Math.min(lo, v); hi = Math.max(hi, v);
  }
  for (let i = 0; i < res * res; i++) f.data[i] = (f.data[i]! - lo) / (hi - lo + 1e-9);
  return finishHeight(f, res, 0, island);
}

function finishHeight(f: Field, res: number, smooth: number, island: number): Field {
  let g = f.width === res && f.height === res ? f : resampleField(f, res, res);
  if (smooth > 0) g = gaussianBlur(g, smooth);
  if (island > 0) {
    for (let y = 0; y < res; y++) for (let x = 0; x < res; x++) {
      const dx = (x / (res - 1)) * 2 - 1, dy = (y / (res - 1)) * 2 - 1;
      const r = Math.sqrt(dx * dx + dy * dy);
      const fall = Math.max(0, 1 - Math.max(0, (r - (1 - island * 0.9)) / (island * 0.9 + 1e-6)));
      const k = fall * fall * (3 - 2 * fall);
      g.data[y * res + x] = g.data[y * res + x]! * (island > 0 ? k : 1);
    }
  }
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < res * res; i++) { lo = Math.min(lo, g.data[i]!); hi = Math.max(hi, g.data[i]!); }
  for (let i = 0; i < res * res; i++) g.data[i] = (g.data[i]! - lo) / (hi - lo + 1e-9);
  return g;
}

function mulberry(seed: number) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/** 높이장 → 지오메트리. 정점 (x,z) 는 [-size/2, size/2], y = h·heightScale. UV 는 0..1. */
export function buildTerrainGeometry(field: Field, size: number, heightScale: number): THREE.BufferGeometry {
  const res = field.width;
  const geo = new THREE.PlaneGeometry(size, size, res - 1, res - 1);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = i % res, y = Math.floor(i / res);
    pos.setY(i, field.data[y * res + x]! * heightScale);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  geo.computeBoundingBox();
  return geo;
}

/** 경사(0=평지, 1=수직) 장. 정규화 높이·heightScale·size 로 실제 기울기를 잰다. */
export function slopeField(field: Field, size: number, heightScale: number): Field {
  const res = field.width;
  const step = size / (res - 1);
  const s = makeField(res, res);
  for (let y = 0; y < res; y++) for (let x = 0; x < res; x++) {
    const h = (xx: number, yy: number) => field.data[Math.min(res - 1, Math.max(0, yy)) * res + Math.min(res - 1, Math.max(0, xx))]! * heightScale;
    const gx = (h(x + 1, y) - h(x - 1, y)) / (2 * step);
    const gy = (h(x, y + 1) - h(x, y - 1)) / (2 * step);
    const g = Math.sqrt(gx * gx + gy * gy);
    s.data[y * res + x] = g / Math.sqrt(1 + g * g); // sin(경사각)
  }
  return s;
}

export interface SplatWeights { grass: number; dirt: number; rock: number; snow: number }

/** 높이·경사 → 네 층 가중치(합 1). 문턱은 params. */
export function splatWeights(h: number, slope: number, p: TerrainParams): SplatWeights {
  const sm = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a + 1e-6))); return t * t * (3 - 2 * t); };
  const rock = sm(p.rockSlope - 0.15, p.rockSlope + 0.15, slope);
  const snow = sm(p.snowHeight - 0.08, p.snowHeight + 0.08, h) * (1 - rock * 0.6);
  const dirt = sm(p.sandHeight + 0.06, p.sandHeight - 0.06, h); // 낮은 곳은 흙/모래
  const grass = 1;
  let w = { grass, dirt: dirt * (1 - rock), rock, snow };
  // 우선순위: snow > rock > dirt > grass
  const rest1 = 1 - w.snow; w.rock = Math.min(w.rock, rest1);
  const rest2 = rest1 - w.rock; w.dirt = Math.min(w.dirt, rest2);
  w.grass = Math.max(0, rest2 - w.dirt);
  const sum = w.grass + w.dirt + w.rock + w.snow || 1;
  w = { grass: w.grass / sum, dirt: w.dirt / sum, rock: w.rock / sum, snow: w.snow / sum };
  return w;
}

export type LayerSampler = (u: number, v: number) => [number, number, number];

/** 타일 이미지 → 반복 샘플러(u,v 0..1, 타일링 배수 적용). 캔버스 없는 환경에서도 도는 순수 함수. */
export function layerSampler(r: Raster, tiling: number): LayerSampler {
  const { width: w, height: h, data } = r;
  return (u, v) => {
    const x = Math.floor(((u * tiling) % 1 + 1) % 1 * w) % w;
    const y = Math.floor(((v * tiling) % 1 + 1) % 1 * h) % h;
    const i = (y * w + x) * 4;
    return [data[i]!, data[i + 1]!, data[i + 2]!];
  };
}

const FALLBACK_COLORS: Record<keyof SplatWeights, [number, number, number]> = {
  grass: [96, 142, 58], dirt: [150, 118, 78], rock: [118, 114, 112], snow: [236, 240, 245],
};

/**
 * 스플랫 알베도 굽기 — 텍셀마다 높이·경사 → 가중치 → 네 층 샘플을 섞는다.
 * 층 이미지가 없으면 단색. 물 아래는 살짝 푸르게 눌러 수심을 보여준다.
 */
export function bakeSplatAlbedo(field: Field, p: TerrainParams, layers: Partial<Record<keyof SplatWeights, LayerSampler>>, texSize = 1024): Raster {
  const slope = slopeField(field, p.size, p.heightScale);
  const out = { width: texSize, height: texSize, data: new Uint8ClampedArray(texSize * texSize * 4) } as Raster;
  for (let y = 0; y < texSize; y++) {
    const v = y / (texSize - 1);
    for (let x = 0; x < texSize; x++) {
      const u = x / (texSize - 1);
      // PlaneGeometry 의 UV 는 v=1 이 -z(위쪽 행) — 우리 field 는 y=0 이 첫 행. rotateX 후 첫 행이 -z 이므로 v 를 뒤집는다.
      const h = sampleField(field, u, 1 - v);
      const s = sampleField(slope, u, 1 - v);
      const w = splatWeights(h, s, p);
      let r = 0, g = 0, b = 0;
      for (const k of ['grass', 'dirt', 'rock', 'snow'] as const) {
        const wk = w[k]; if (wk <= 0) continue;
        const c = layers[k] ? layers[k]!(u, v) : FALLBACK_COLORS[k];
        r += c[0] * wk; g += c[1] * wk; b += c[2] * wk;
      }
      // 미세 변화 — 균일한 단색이 되지 않게 높이로 살짝 명암
      const shade = 0.9 + 0.2 * h;
      if (p.waterLevel > 0 && h < p.waterLevel) { const d = Math.min(1, (p.waterLevel - h) / 0.12); r *= 1 - 0.35 * d; g *= 1 - 0.15 * d; b = b * (1 - 0.05 * d) + 40 * d; }
      const i = (y * texSize + x) * 4;
      out.data[i] = r * shade; out.data[i + 1] = g * shade; out.data[i + 2] = b * shade; out.data[i + 3] = 255;
    }
  }
  return out;
}

/** 월드 (x,z) 의 지형 높이 — 캐릭터를 땅 위에 세울 때. */
export function heightAt(field: Field, size: number, heightScale: number, x: number, z: number): number {
  const u = x / size + 0.5, v = z / size + 0.5;
  if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
  return sampleField(field, u, v) * heightScale;
}
